/**
 * The published holiday calendar and working-day maths — ADR-005 §3–§6, operator decision 8 (seed the
 * Maharashtra list; HR defines and publishes each year's calendar: fixed and optional holidays).
 *
 * Who: HR (`holidays.manage` — ADR-005's `hr.admin`) writes drafts and publishes; everyone reads the
 * published calendar; drafts are visible to HR only (404 otherwise). Managers administer optional-holiday
 * elections with HR. Every write is audited.
 */
import { z } from 'zod';
import { IsoDate, Timestamp, Uuid, Version, list } from './common.js';
import { UserRef } from './users.js';
export const CalendarStatus = z.enum(['draft', 'published', 'superseded']).meta({ id: 'CalendarStatus' });
export const HolidayDayType = z
    .object({
    key: z.string(),
    label: z.string(),
    /** `zero` = no hours expected; `half` = half the working-day hours; `none` = a normal day. */
    expected_hours_effect: z.enum(['none', 'zero', 'half']),
    expected_hours_override: z.number().nullable(),
    /** Optional holidays: off only for people who elect them. */
    requires_opt_in: z.boolean(),
    is_working_day: z.boolean(),
    counts_against_cap: z.boolean(),
    active: z.boolean(),
    sort_order: z.number().int(),
})
    .meta({ id: 'HolidayDayType' });
export const HolidayDayTypeList = list(HolidayDayType).meta({ id: 'HolidayDayTypeList' });
export const DayTypeKeyParams = z.object({ key: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/) });
export const CreateDayTypeBody = z
    .object({
    key: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/, 'Use lower-case letters, digits and _'),
    label: z.string().trim().min(2).max(60),
    expected_hours_effect: z.enum(['none', 'zero', 'half']),
    expected_hours_override: z.number().min(0).max(24).nullable().optional(),
    requires_opt_in: z.boolean().optional(),
    is_working_day: z.boolean().optional(),
    counts_against_cap: z.boolean().optional(),
    sort_order: z.number().int().min(0).max(1000).optional(),
})
    .strict();
export const UpdateDayTypeBody = CreateDayTypeBody.omit({ key: true }).partial().extend({ active: z.boolean().optional() }).strict()
    .refine((b) => Object.keys(b).length > 0, { message: 'Change at least one field' });
export const HolidayDay = z
    .object({ id: Uuid, calendar_id: Uuid, the_date: IsoDate, name: z.string(), day_type: z.string(), note: z.string().nullable() })
    .meta({ id: 'HolidayDay' });
export const HolidayCalendar = z
    .object({
    id: Uuid,
    year: z.number().int(),
    /** Default `MH` (Maharashtra). One published calendar per year and region. */
    region: z.string(),
    status: CalendarStatus,
    version: Version,
    /** How many optional holidays each person may take. */
    optional_allowance: z.number().int().min(0),
    note: z.string().nullable(),
    created_by: UserRef.nullable(),
    published_by: UserRef.nullable(),
    published_at: Timestamp.nullable(),
    superseded_by_id: Uuid.nullable(),
    /** Present on single reads. */
    days: z.array(HolidayDay).optional(),
})
    .meta({ id: 'HolidayCalendar' });
export const HolidayCalendarList = list(HolidayCalendar).meta({ id: 'HolidayCalendarList' });
export const CalendarIdParams = z.object({ id: Uuid });
export const CalendarDayParams = z.object({ id: Uuid, day_id: Uuid });
export const ListCalendarsQuery = z.object({ year: z.coerce.number().int().min(2020).max(2100).optional(), status: CalendarStatus.optional() });
/** `copy_from_id` clones a calendar's days (shifted to the new year where the date exists) as a starting point. */
export const CreateCalendarBody = z
    .object({
    year: z.number().int().min(2020).max(2100),
    region: z.string().regex(/^[A-Z]{2,3}$/).default('MH'),
    optional_allowance: z.number().int().min(0).max(20).default(2),
    note: z.string().trim().max(500).optional(),
    copy_from_id: Uuid.optional(),
})
    .strict();
/** Drafts only (409 on a published calendar — amendments are a new draft that supersedes on publish). */
export const UpdateCalendarBody = z
    .object({ optional_allowance: z.number().int().min(0).max(20).optional(), note: z.string().trim().max(500).nullable().optional() })
    .strict()
    .refine((b) => Object.keys(b).length > 0, { message: 'Change at least one field' });
export const HolidayDayBody = z
    .object({
    the_date: IsoDate,
    name: z.string().trim().min(2).max(80),
    day_type: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/),
    note: z.string().trim().max(300).optional(),
})
    .strict();
export const UpdateHolidayDayBody = HolidayDayBody.partial().strict().refine((b) => Object.keys(b).length > 0, { message: 'Change at least one field' });
/**
 * Publish: validates (dates inside the year, known active day types, no weekend-only list), supersedes the
 * previously published calendar for that year/region, and reports what changes. Working-day maths for
 * affected dates is recomputed (timesheet expectations, the gate, request clocks).
 */
/**
 * An optional-holiday election that cannot survive an amendment (its day was removed or is no longer an
 * optional day): publishing withdraws it, so the person owes that day again. Reported, never silent.
 */
export const WithdrawnElection = z
    .object({ user: UserRef, date: IsoDate, name: z.string(), re_gated: z.boolean() })
    .meta({ id: 'WithdrawnElection' });
export const PublishResponse = z
    .object({
    calendar: HolidayCalendar,
    version: z.number().int(),
    /** Dates whose expected hours change for anyone — including dates of withdrawn elections. */
    affected_dates: z.array(IsoDate),
    affected_users: z.number().int().nonnegative(),
    recompute_job_id: z.string().nullable(),
    /** Elections moved to the new calendar's day with the same date (no change for the person). */
    carried_elections: z.number().int().nonnegative(),
    withdrawn_elections: z.array(WithdrawnElection),
})
    .meta({ id: 'PublishResponse' });
/**
 * Publish preview (ADR-005 §5: HR sees the blast radius BEFORE confirming). Same validation as publish;
 * changes nothing. `field_errors` lists what would block publishing (empty = it would publish).
 */
export const PublishPreview = z
    .object({
    can_publish: z.boolean(),
    field_errors: z.array(z.object({ path: z.string(), message: z.string() })),
    /** Dates whose expected hours change compared with the live calendar for that year. */
    affected_dates: z.array(IsoDate),
    affected_users: z.number().int().nonnegative(),
    /** Calendar it would supersede, if any. */
    supersedes_id: Uuid.nullable(),
    carried_elections: z.number().int().nonnegative(),
    withdrawn_elections: z.array(WithdrawnElection),
})
    .meta({ id: 'PublishPreview' });
// ---------------------------------------------------------------- optional holidays
export const OptionalElection = z
    .object({
    id: Uuid,
    user: UserRef,
    holiday_day: HolidayDay,
    elected_at: Timestamp,
    elected_by: UserRef,
})
    .meta({ id: 'OptionalElection' });
export const MyOptionalHolidays = z
    .object({
    year: z.number().int(),
    allowance: z.number().int(),
    used: z.number().int(),
    remaining: z.number().int(),
    available: z.array(z.object({
        day: HolidayDay,
        elected: z.boolean(),
        election_id: Uuid.nullable(),
        /** Elections are editable until the end of the following month, then by HR/managers only (H6). */
        editable: z.boolean(),
    })),
})
    .meta({ id: 'MyOptionalHolidays' });
export const MyOptionalQuery = z.object({ year: z.coerce.number().int().min(2020).max(2100).optional() });
export const ElectBody = z.object({ holiday_day_id: Uuid }).strict();
export const ElectionIdParams = z.object({ id: Uuid });
export const WithdrawResponse = z
    .object({
    /** `true` when withdrawing makes a past day expected again that the person never filed (gate returns). */
    re_gated: z.boolean(),
})
    .meta({ id: 'WithdrawResponse' });
export const OptionalAdminRow = z
    .object({ user: UserRef, elected: z.array(HolidayDay), used: z.number().int(), remaining: z.number().int() })
    .meta({ id: 'OptionalAdminRow' });
export const OptionalAdminList = list(OptionalAdminRow).meta({ id: 'OptionalAdminList' });
export const UserIdOnlyParams = z.object({ id: Uuid });
export const UserElectionParams = z.object({ id: Uuid, election_id: Uuid });
// ---------------------------------------------------------------- working days (one endpoint, so the UI never recomputes)
export const WorkingDay = z
    .object({
    date: IsoDate,
    expected_hours: z.number().min(0).max(24),
    kind: z.enum(['working', 'weekend', 'holiday', 'optional_taken', 'optional_available', 'half_day']),
    holiday_name: z.string().nullable(),
})
    .meta({ id: 'WorkingDay' });
/** At most 92 days per call. `user_id` other than yourself: managers and HR only (404 otherwise). */
export const WorkingDaysQuery = z
    .object({ from: IsoDate, to: IsoDate, user_id: Uuid.optional() })
    .refine((q) => q.from <= q.to, { path: ['to'], message: 'to must be on or after from' })
    .refine((q) => (Date.parse(q.to) - Date.parse(q.from)) / 86_400_000 <= 92, { path: ['to'], message: 'At most 92 days at a time' });
export const WorkingDaysResponse = z.object({ user: UserRef, days: z.array(WorkingDay) }).meta({ id: 'WorkingDaysResponse' });
//# sourceMappingURL=holidays.js.map